import type { SVGProps } from 'react';

export type AdminIconName =
  | 'dashboard'
  | 'forms'
  | 'designer'
  | 'submissions'
  | 'reports'
  | 'settings'
  | 'sms'
  | 'access'
  | 'external'
  | 'plus'
  | 'menu'
  | 'collapse'
  | 'edit'
  | 'copy'
  | 'search'
  | 'refresh'
  | 'active'
  | 'responses'
  | 'capacity'
  | 'calendar'
  | 'language'
  | 'chevron-right'
  | 'chevron-left';

interface Props extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: AdminIconName;
  size?: number;
}

export function AdminIcon({ name, size = 20, ...props }: Props) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    focusable: false,
    'aria-hidden': true
  };

  const paths = (() => {
    switch (name) {
      case 'dashboard':
        return <><rect x="3" y="3" width="7" height="7" rx="2"/><rect x="14" y="3" width="7" height="7" rx="2"/><rect x="3" y="14" width="7" height="7" rx="2"/><rect x="14" y="14" width="7" height="7" rx="2"/></>;
      case 'forms':
        return <><path d="M7 3.5h8l4 4V20a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 20V5a1.5 1.5 0 0 1 1.5-1.5Z"/><path d="M15 3.5V8h4"/><path d="M8.5 12h7M8.5 16h7"/></>;
      case 'designer':
        return <><path d="m4 20 4.3-1 10.9-10.9a2.2 2.2 0 0 0-3.1-3.1L5.2 15.9 4 20Z"/><path d="m14.7 6.4 3 3"/><path d="M4 20h4.3"/></>;
      case 'submissions':
        return <><rect x="3" y="5" width="18" height="15" rx="2"/><path d="M8 3v4M16 3v4M3 10h18"/><path d="m8 15 2.1 2L16 12"/></>;
      case 'reports':
        return <><path d="M4 20V10M10 20V4M16 20v-7M22 20H2"/><path d="m3 7 6-4 6 5 6-5"/></>;
      case 'access':
        return <><path d="M12 3 5 6v5c0 4.8 2.8 8.1 7 10 4.2-1.9 7-5.2 7-10V6l-7-3Z"/><path d="M9.5 12.2 11 13.7l3.8-4"/></>;
      case 'settings':
        return <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.1 3.6-.2-.1a1.7 1.7 0 0 0-1.9-.2l-.5.2a1.7 1.7 0 0 0-1 1.5v.3H9.9V22a1.7 1.7 0 0 0-1-1.5l-.5-.2a1.7 1.7 0 0 0-1.9.2l-.2.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9l-.2-.5A1.7 1.7 0 0 0 2.9 13h-.2V9h.2a1.7 1.7 0 0 0 1.5-1l.2-.5a1.7 1.7 0 0 0-.3-1.9l-.1-.1 2.1-3.6.2.1a1.7 1.7 0 0 0 1.9.2l.5-.2a1.7 1.7 0 0 0 1-1.5V.2h4.2v.3a1.7 1.7 0 0 0 1 1.5l.5.2a1.7 1.7 0 0 0 1.9-.2l.2-.1 2.1 3.6-.1.1a1.7 1.7 0 0 0-.3 1.9l.2.5a1.7 1.7 0 0 0 1.5 1h.2v4H21a1.7 1.7 0 0 0-1.5 1l-.1.5Z"/></>;
      case 'sms':
        return <><path d="M5 5h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9l-5 3v-4.2A2 2 0 0 1 3 15V7a2 2 0 0 1 2-2Z"/><path d="M7.5 9.5h9M7.5 13h6"/></>;
      case 'external':
        return <><path d="M14 4h6v6M20 4l-9 9"/><path d="M19 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h6"/></>;
      case 'plus':
        return <><path d="M12 5v14M5 12h14"/></>;
      case 'menu':
        return <><path d="M4 6h16M4 12h16M4 18h16"/></>;
      case 'collapse':
        return <><path d="m14 7-5 5 5 5"/><path d="M20 4v16"/></>;
      case 'edit':
        return <><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4L16.5 3.5Z"/></>;
      case 'copy':
        return <><rect x="8" y="8" width="12" height="12" rx="2"/><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2"/></>;
      case 'search':
        return <><circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/></>;
      case 'refresh':
        return <><path d="M20 6v5h-5"/><path d="M4 18v-5h5"/><path d="M6.1 9A7 7 0 0 1 18 6l2 5M4 13l2 5a7 7 0 0 0 11.9-3"/></>;
      case 'active':
        return <><circle cx="12" cy="12" r="9"/><path d="m8 12 2.5 2.5L16 9"/></>;
      case 'responses':
        return <><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8Z"/><path d="M8 8h8M8 12h6"/></>;
      case 'capacity':
        return <><path d="M4 20V10M10 20V4M16 20v-7M22 20H2"/></>;
      case 'calendar':
        return <><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M16 3v4M8 3v4M3 10h18"/></>;
      case 'language':
        return <><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/></>;
      case 'chevron-left':
        return <path d="m15 18-6-6 6-6"/>;
      case 'chevron-right':
        return <path d="m9 18 6-6-6-6"/>;
      default:
        return null;
    }
  })();

  return <svg {...common} {...props}>{paths}</svg>;
}
